import React from 'react'
import { Button } from './Button'
import type { ButtonHTMLAttributes } from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * LoadingButton Component
 *
 * A button component that extends the base Button component with loading state support.
 * Displays a spinning loader icon when in loading state and automatically disables user interaction.
 *
 * @example
 * // Basic usage with loading state
 * <LoadingButton loading={isSubmitting} variant="primary">
 *   Save Changes
 * </LoadingButton>
 *
 * @example
 * // Form submission with loading state
 * const [isLoading, setIsLoading] = useState(false)
 *
 * const handleSubmit = async () => {
 *   setIsLoading(true)
 *   try {
 *     await saveData()
 *   } finally {
 *     setIsLoading(false)
 *   }
 * }
 *
 * <LoadingButton loading={isLoading} onClick={handleSubmit} variant="primary">
 *   Submit Form
 * </LoadingButton>
 *
 * @example
 * // With all button variants
 * <LoadingButton loading={loading} variant="primary">Primary Action</LoadingButton>
 * <LoadingButton loading={loading} variant="secondary">Secondary Action</LoadingButton>
 * <LoadingButton loading={loading} variant="ghost">Ghost Action</LoadingButton>
 * <LoadingButton loading={loading} variant="danger">Delete</LoadingButton>
 * <LoadingButton loading={loading} variant="outline">Outlined</LoadingButton>
 * <LoadingButton loading={loading} variant="default">Default</LoadingButton>
 * <LoadingButton loading={loading} variant="link">Link Style</LoadingButton>
 */

export interface LoadingButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Button variant style
   * @default 'primary'
   */
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline' | 'default' | 'link'

  /**
   * Button size
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg'

  /**
   * Whether the button is in a loading state
   * When true, displays a spinner icon and disables the button
   * @default false
   */
  loading?: boolean

  /**
   * Button content
   */
  children: React.ReactNode
}

/**
 * LoadingButton component that wraps the base Button with loading state functionality
 */
export const LoadingButton = React.forwardRef<HTMLButtonElement, LoadingButtonProps>(
  ({ loading = false, children, disabled, className, variant = 'primary', size = 'md', ...props }, ref) => {
    return (
      <Button
        ref={ref}
        disabled={disabled || loading}
        className={className}
        variant={variant}
        size={size}
        {...props}
      >
        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {children}
      </Button>
    )
  }
)

LoadingButton.displayName = 'LoadingButton'
